// Take a claim off the submit queue. A reason is required: the row is kept with
// who removed it, when and why, so "why is this claim not sent?" has an answer.
//
// Focus goes to the reason, never to the confirm button, and Escape closes.

import React, { useEffect, useRef, useState } from "react";
import { removeItem } from "./api";
import useReturnFocus from "./useReturnFocus";
import "./Queue.css";

export default function RemoveModal({
  practiceId,
  item,
  onClose,
  onStale,
  onRemoved,
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const reasonRef = useRef(null);

  useReturnFocus(reasonRef);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  const valid = reason.trim().length > 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await removeItem(practiceId, item.id, reason.trim());
      onRemoved(item);
    } catch (err) {
      setError(err.message);
      setBusy(false);
      // 409: the row changed under this page (someone else acted on it). Refresh
      // the list behind the modal so the row stops offering stale actions.
      if (err.status === 409 && onStale) onStale();
      // The fields were disabled while busy, which drops focus to <body>; give it
      // back so a keyboard user can correct and retry.
      setTimeout(() => reasonRef.current?.focus(), 0);
    }
  };

  return (
    <div className="sq-modal-backdrop" onClick={() => !busy && onClose()}>
      <div
        className="sq-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sq-remove-title"
      >
        <div className="sq-modal-header">
          <h2 id="sq-remove-title">Remove from the queue</h2>
          <button
            className="sq-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="sq-modal-body">
          <div className="sq-modal-summary">
            <span>
              Claim <strong>#{item.claimId}</strong>
            </span>
            <span>DOS {item.dateOfService || "—"}</span>
            <span>{item.payerName || "—"}</span>
          </div>

          <p className="sq-modal-lede">
            The claim goes back to the ledger unsent. Nothing is deleted: the
            queue keeps who removed it and why.
          </p>

          <label className="sq-field">
            <span>Reason</span>
            <textarea
              ref={reasonRef}
              rows={3}
              maxLength={1000}
              value={reason}
              placeholder="e.g. payer wants a paper claim; duplicate of #1234"
              disabled={busy}
              onChange={(e) => setReason(e.target.value)}
            />
            <small className="sq-field-hint">
              Shown in the queue list. Claim ids and payer detail only — no
              patient names.
            </small>
          </label>

          {error && (
            <div className="sq-message error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="sq-modal-footer">
          <button
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger btn-sm sq-danger"
            onClick={submit}
            disabled={!valid || busy}
          >
            {busy ? "Removing…" : `Remove claim #${item.claimId}`}
          </button>
        </div>
      </div>
    </div>
  );
}
